import { Injectable } from '@angular/core';

interface CloseableItem {
  close: () => void;
}

/**
 * Manages ESC key handling for drawers and modals.
 * When multiple items are stacked, ESC closes only the topmost one.
 *
 * @example
 * private unregisterEsc = () => {};
 *
 * ngOnInit() {
 *   this.unregisterEsc = this.escapeKeyService.register(() => this.close());
 * }
 *
 * ngOnDestroy() {
 *   this.unregisterEsc();
 * }
 */
@Injectable({
  providedIn: 'root'
})
export class EscapeKeyService {
  private stack: CloseableItem[] = [];
  private isListening = false;
  private boundEscapeHandler: ((event: KeyboardEvent) => void) | null = null;

  constructor() {}

  /**
   * Register a closeable item to respond to ESC key.
   * Returns an unregister function that can be called multiple times safely.
   */
  register(closeCallback: () => void): () => void {
    const item: CloseableItem = { close: closeCallback };
    this.stack.push(item);
    this.ensureListening();

    let unregistered = false;
    return () => {
      if (unregistered) return;
      unregistered = true;

      const index = this.stack.indexOf(item);
      if (index !== -1) {
        this.stack.splice(index, 1);
      }

      if (this.stack.length === 0) {
        this.stopListening();
      }
    };
  }

  getStackSize(): number {
    return this.stack.length;
  }

  clear(): void {
    this.stack = [];
    this.stopListening();
  }

  private handleEscape(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || this.stack.length === 0) return;

    // If a CDK overlay (mat-select panel, mat-menu, mat-autocomplete, etc.) is open,
    // let Angular Material handle the Escape key rather than closing the drawer.
    if (this.isCdkOverlayOpen()) return;

    const topItem = this.stack[this.stack.length - 1];
    this.stack.pop();
    topItem.close();

    if (this.stack.length === 0) {
      this.stopListening();
    }

    event.preventDefault();
    event.stopPropagation();
  }

  private isCdkOverlayOpen(): boolean {
    const container = document.querySelector('.cdk-overlay-container');
    return !!(container && container.querySelector('.cdk-overlay-pane'));
  }

  private ensureListening(): void {
    if (!this.isListening) {
      this.boundEscapeHandler = this.handleEscape.bind(this);
      document.addEventListener('keydown', this.boundEscapeHandler, true);
      this.isListening = true;
    }
  }

  private stopListening(): void {
    if (this.isListening && this.boundEscapeHandler) {
      document.removeEventListener('keydown', this.boundEscapeHandler, true);
      this.boundEscapeHandler = null;
      this.isListening = false;
    }
  }
}
